/**
 * slug-preview.ts
 *
 * Mirrors C# {@link UrlSegmentGenerator} logic in the browser so that the URL Segment
 * field is updated in real time as the user types the school name.
 *
 * Behaviour:
 *  - The Name input drives auto-update of the URL Segment field.
 *  - Auto-update pauses once the user manually edits the URL Segment field.
 *  - Auto-update resumes if the user clears the URL Segment field.
 *
 * The algorithm must match {@code SgoPortal.Core.Schools.UrlSegmentGenerator.Generate}
 * exactly so that the preview is consistent with what the server produces.
 */

/**
 * Converts a school name into a URL-safe kebab-case slug.
 * Mimics UrlSegmentGenerator.Generate from the C# Core layer.
 */
function generateSlug(name: string): string {
  if (!name.trim()) return '';

  // NFD Unicode normalization — expand diacritics (ä → a + combining mark)
  const normalized = name.normalize('NFD');

  // Remove combining diacritical marks (Unicode category Mn)
  const withoutDiacritics = normalized.replace(/[\u0300-\u036f]/g, '');

  // Allow only ASCII characters (for parity with C# char < 128 check)
  const asciiOnly = withoutDiacritics.replace(/[^\x00-\x7F]/g, '');

  let slug = asciiOnly
    .toLowerCase()
    .replace(/ /g, '-')           // spaces → hyphens
    .replace(/[^a-z0-9-]/g, '')  // strip non-alphanumeric-hyphen chars
    .replace(/-{2,}/g, '-')      // collapse consecutive hyphens
    .replace(/^-+|-+$/g, '');    // trim leading/trailing hyphens

  return slug;
}

/**
 * Wires the slug-preview behaviour between the name input and URL segment input.
 *
 * @param nameSelector    CSS selector for the school-name input (e.g. `"#schoolName"`).
 * @param segmentSelector CSS selector for the URL-segment input (e.g. `"#urlSegment"`).
 */
export function initSlugPreview(
  nameSelector: string,
  segmentSelector: string
): void {
  const nameInput = document.querySelector<HTMLInputElement>(nameSelector);
  const segmentInput = document.querySelector<HTMLInputElement>(segmentSelector);

  if (!nameInput || !segmentInput) return;

  // True while the user has manually edited the segment field.
  let userHasEditedSegment = false;

  // Detect manual edits on the segment field.
  segmentInput.addEventListener('input', () => {
    if (segmentInput.value === '') {
      // User cleared the field — resume auto-generation.
      userHasEditedSegment = false;
    } else {
      // Pause auto-generation so we don't overwrite the user's own slug.
      userHasEditedSegment = true;
    }
  });

  // Update the segment preview when the name changes.
  nameInput.addEventListener('input', () => {
    if (userHasEditedSegment) return;
    segmentInput.value = generateSlug(nameInput.value);
  });
}
